import { useState } from "react";
import { motion } from "framer-motion";
import { Wrench, Check, Loader2, ArrowRight, ShieldCheck, DollarSign, Package, Rocket } from "lucide-react";
import { toast } from "sonner";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";
import Seo from "@/components/Seo";
import DiscordCta from "@/components/DiscordCta";
import api, { formatApiErrorDetail } from "@/lib/api";

/* Public modder early-access portal — apply to build on New Citizen's modding system
   before release, with paid Fab contractor bounties. Inbound = consented contact. */

const EASE = [0.16, 1, 0.3, 1];
const fade = (d = 0) => ({ initial: { opacity: 0, y: 20 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-60px" }, transition: { duration: 0.6, delay: d, ease: EASE } });
const inputCls = "w-full bg-[#050505] border border-white/10 px-4 py-3 font-mono text-sm text-zinc-100 rounded-none focus:outline-none focus:ring-1 focus:ring-nc-blue focus:border-nc-blue placeholder:text-zinc-600";
const labelCls = "font-mono text-[10px] tracking-[0.25em] text-zinc-500 uppercase block mb-1.5";

const PERKS = [
  { icon: Rocket, title: "Build before release", body: "Get into the Mod Workshop while the paint is still wet — your content can be live in Bornwood on day one." },
  { icon: DollarSign, title: "Paid bounties (AUD)", body: "The Fab contractor pipeline pays real money per approved pack — vehicles, props, clothing, entire systems." },
  { icon: Package, title: "Your own distribution", body: "Ship through the Mod Workshop to every server on the network. Server owners pull your packs with one click." },
  { icon: Wrench, title: "Real tooling", body: "UE5-native pipeline, SDK docs, Dev Hub, and a QA team that actually reviews and ships your work." },
];

export default function ModdersPage() {
  const [form, setForm] = useState({ name: "", email: "", portfolio_url: "", skills: "", experience: "", message: "", consent_updates: false });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await api.post("/modders/apply", {
        name: form.name.trim(), email: form.email.trim(), portfolio_url: form.portfolio_url.trim(),
        skills: form.skills.trim(), experience: form.experience.trim(), message: form.message.trim(),
        consent_updates: form.consent_updates,
      });
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not submit — try again");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="modders-page" className="min-h-screen bg-[#050505] text-zinc-100">
      <Seo title="Modder Early Access — Build for New Citizen Before Release" description="Modders: get early access to New Citizen's UE5 modding system, ship through the Mod Workshop at launch, and earn paid AUD bounties for approved packs." path="/modders" keywords="New Citizen mods, UE5 modding, roleplay game modding, mod workshop" />
      <SiteNav />

      <section className="relative border-b border-white/10 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(255,107,26,0.10),transparent_60%)]" />
        <div className="relative max-w-5xl mx-auto px-6 py-20 sm:py-28">
          <motion.p {...fade(0)} className="font-mono text-[10px] tracking-[0.4em] text-nc-orange uppercase">New Citizen // Mod Workshop</motion.p>
          <motion.h1 {...fade(0.05)} className="font-sans font-extrabold uppercase tracking-tight text-4xl sm:text-5xl lg:text-6xl text-white mt-4 leading-[1.05]">
            Build the city<br /><span className="text-nc-orange">before it opens.</span>
          </motion.h1>
          <motion.p {...fade(0.1)} className="mt-6 max-w-2xl text-base sm:text-lg text-zinc-400 leading-relaxed">
            New Citizen is a UE5 roleplay platform with a real modding system — not a workaround. We're hand-picking
            founding modders for early access: your vehicles, props, clothing and systems, shipped through the Mod Workshop
            at launch, with paid bounties for approved work.
          </motion.p>
          <motion.div {...fade(0.15)} className="mt-8 flex flex-wrap gap-3 items-center">
            <a href="#apply" data-testid="modders-hero-apply" className="bg-nc-orange hover:bg-nc-orange/80 text-white font-sans font-semibold uppercase tracking-[0.2em] text-xs px-8 py-4 transition-colors flex items-center gap-2">
              Apply for early access <ArrowRight className="w-4 h-4" />
            </a>
            <div className="w-full sm:max-w-sm mt-2 sm:mt-0"><DiscordCta testId="modders-discord" compact /></div>
          </motion.div>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-6 py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-white/10 border border-white/10">
          {PERKS.map((p, i) => (
            <motion.div key={p.title} {...fade(i * 0.05)} className="bg-[#050505] p-8">
              <p.icon className="w-5 h-5 text-nc-orange" />
              <h3 className="font-sans font-bold uppercase tracking-widest text-sm text-zinc-100 mt-4">{p.title}</h3>
              <p className="font-mono text-xs text-zinc-500 leading-relaxed mt-2">{p.body}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section id="apply" className="max-w-3xl mx-auto px-6 pb-24">
        {done ? (
          <div data-testid="modder-success" className="border border-nc-green/40 bg-nc-green/5 p-10 text-center">
            <Check className="w-10 h-10 text-nc-green mx-auto" />
            <h2 className="font-sans font-bold uppercase tracking-widest text-xl text-white mt-4">Application received</h2>
            <p className="font-mono text-xs text-zinc-400 leading-relaxed mt-3 max-w-md mx-auto">
              Confirmation is on its way to your inbox. We read every application by hand — if your portfolio shows real craft,
              an invite with Fab contractor access follows. Create your Citizen Hub account with the same email and access unlocks automatically.
            </p>
          </div>
        ) : (
          <motion.form {...fade(0)} data-testid="modder-form" onSubmit={submit} className="border border-white/10 bg-black/40 p-8 sm:p-10 flex flex-col gap-5">
            <div className="flex items-center gap-3">
              <Wrench className="w-5 h-5 text-nc-orange" />
              <h2 className="font-sans font-bold uppercase tracking-widest text-lg text-white">Apply for early access</h2>
            </div>
            <p className="font-mono text-[11px] text-zinc-500 leading-relaxed">
              Tell us what you build. Links to ArtStation, Sketchfab, GitHub, Steam Workshop or a portfolio site carry the most weight.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div><label className={labelCls}>Name / handle *</label>
                <input data-testid="modder-name" required minLength={2} className={inputCls} value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g. polyforge" /></div>
              <div><label className={labelCls}>Email *</label>
                <input data-testid="modder-email" required type="email" className={inputCls} value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@example.com" /></div>
              <div className="sm:col-span-2"><label className={labelCls}>Portfolio link</label>
                <input data-testid="modder-portfolio" type="url" className={inputCls} value={form.portfolio_url} onChange={(e) => set("portfolio_url", e.target.value)} placeholder="https://artstation.com/you" /></div>
              <div className="sm:col-span-2"><label className={labelCls}>What do you build? *</label>
                <input data-testid="modder-skills" required minLength={3} className={inputCls} value={form.skills} onChange={(e) => set("skills", e.target.value)} placeholder="3D vehicles, PBR texturing, clothing, weapons, interiors, systems…" /></div>
              <div className="sm:col-span-2"><label className={labelCls}>Experience</label>
                <textarea data-testid="modder-experience" rows={3} className={inputCls} value={form.experience} onChange={(e) => set("experience", e.target.value)} placeholder="Engines you've shipped for (UE, Unity, RAGE/FiveM, Source), released mods, team or solo…" /></div>
              <div className="sm:col-span-2"><label className={labelCls}>Anything else? (optional)</label>
                <textarea data-testid="modder-message" rows={2} className={inputCls} value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="What you'd want to build first for Bornwood…" /></div>
            </div>
            <label data-testid="modder-consent" className="flex items-start gap-3 cursor-pointer select-none">
              <input type="checkbox" checked={form.consent_updates} onChange={(e) => set("consent_updates", e.target.checked)} className="mt-0.5 accent-[#2E6BFF]" />
              <span className="font-mono text-[11px] text-zinc-400 leading-relaxed">Email me New Citizen release updates (optional — unsubscribe any time).</span>
            </label>
            <button data-testid="modder-submit" type="submit" disabled={busy} className="self-start bg-nc-orange hover:bg-nc-orange/80 disabled:opacity-40 text-white font-sans font-semibold uppercase tracking-[0.2em] text-xs px-8 py-4 transition-colors flex items-center gap-2">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wrench className="w-4 h-4" />} {busy ? "Sending…" : "Submit application"}
            </button>
            <p className="font-mono text-[9px] text-zinc-600 leading-relaxed flex items-center gap-2">
              <ShieldCheck className="w-3.5 h-3.5 shrink-0" /> Reviewed by a human. Contractor pipeline is 18+ with ID-verified paid agreements.
            </p>
          </motion.form>
        )}
      </section>

      <SiteFooter />
    </div>
  );
}
